import * as React from 'react'
import handleViewport from 'react-in-viewport'
import dynamic from 'next/dynamic'

const SearchIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/SearchIcon'),
)
import { FacetSelectionInput } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { Checkbox, Input } from '@thg-commerce/gravity-elements'

import { StyledListContainer, StyledListItem } from '../../styles'
import { PriceSliderFacet } from '../FacetComponents'
import { Facet, FacetDataLayerSchema, FacetProps } from '../types'
import {
  isSelectedFacet,
  UpdateFacetAction,
  updateFacetSelection,
  updateRangedFacets,
} from '../utils'

import { BrandInputWrapper, StyledDivContainer, StyledDivItem } from './styles'

type FacetOption = FacetSelectionInput & {
  displayName: string
  matchedProductCount: number
}

export interface FacetContentProps extends FacetProps {
  maxHeight?: string
  isMobile?: boolean
  brandSearch?: {
    state: string
    setState: (searchString: string) => void
  }
  i18nText: {
    searchBrandLabel: string
    searchBrandPlaceholder: string
    minimumLabel: string
    maximumLabel: string
    sliderHeader: string
  }
  currency?: string
  isHorizontalFacet?: boolean
  useGA4EnhancedEcom?: boolean
  facetSchema: FacetDataLayerSchema
  enableAlternateMobileFacet?: boolean
}

const FacetOptionItemInView = ({
  inViewport,
  enterCount,
  contentProps,
  option,
  getStateHandler,
  isSelected,
  forwardedRef,
  index,
}: FaceOptionItem & {
  inViewport: boolean
  enterCount: number
  forwardedRef: React.Ref<any>
}) => {
  return inViewport || enterCount > 0 ? (
    <FacetOptionItem
      contentProps={contentProps}
      option={option}
      getStateHandler={getStateHandler}
      isSelected={isSelected}
      forwardedRef={forwardedRef}
      index={index}
    />
  ) : (
    <li
      id={`facet-option-${index}`}
      tabIndex={0}
      ref={forwardedRef}
      style={{ height: '48px', width: '100%' }}
    />
  )
}

interface FaceOptionItem {
  contentProps: FacetContentProps
  option: FacetOption
  getStateHandler: (state: boolean) => void
  isSelected: boolean
  forwardedRef?: React.Ref<any>
  index?: number
}

const FacetOptionItem = ({
  contentProps,
  option,
  isSelected,
  getStateHandler,
  forwardedRef,
  index,
}: FaceOptionItem) => {
  return (
    <StyledListItem
      data-testid={`facet-option-${option.displayName}`}
      id={`facet-option-${index}`}
      key={`facetcontent-${option.displayName}`}
      isMobile={contentProps.isMobile}
      hasBorder={contentProps.isHorizontalFacet}
      ref={forwardedRef}
      tabIndex={0}
      aria-selected={isSelected}
      role="option"
    >
      <Checkbox
        data-testid={`facet-option-checkbox-${option.displayName}`}
        thinBorder
        transparentBorder
        width="100%"
        thinFocusBorder={contentProps.isHorizontalFacet}
        checked={isSelected}
        label={`${option.displayName} (${option.matchedProductCount})`}
        getState={getStateHandler}
      />
    </StyledListItem>
  )
}

export const FacetContent = React.memo((props: FacetContentProps) => {
  const [brandSearch, setBrandSearch] = React.useState('')
  const brandSearchState = props.brandSearch?.state ?? brandSearch
  const brandSearchSetState = props.brandSearch?.setState ?? setBrandSearch

  const FacetOptionItemInViewComponent = handleViewport<FaceOptionItem>(
    FacetOptionItemInView,
  )

  const getFacetOptionsList = React.useMemo(
    () => (facetOptions: FacetOption[]) => {
      return (
        <StyledListContainer
          data-testid={`facet-container-${props.facet.facetName}`}
          maxHeight={props.maxHeight}
          isMobile={props.isMobile}
          enableAlternateMobileFacet={props.enableAlternateMobileFacet}
        >
          {facetOptions
            .filter((option) => option.matchedProductCount > 0)
            .map((option, index) => {
              const isSelected = isSelectedFacet(
                props.facet.facetName,
                option,
                props.selectedFacets,
              )

              return (props.isMobile ? index >= 10 : index >= 5) ? (
                <FacetOptionItemInViewComponent
                  contentProps={props}
                  option={option}
                  getStateHandler={(state) => {
                    if (state !== isSelected) {
                      const newFacets = updateFacetSelection(
                        state
                          ? UpdateFacetAction.AddFacet
                          : UpdateFacetAction.RemoveFacet,
                        props.facet.facetName,
                        props.facet.facetHeader,
                        option.optionName
                          ? {
                              optionName: option.optionName,
                            }
                          : {
                              from: option.from,
                              to: option.to,
                            },
                        props.selectedFacets,
                        props.useGA4EnhancedEcom,
                        props.facetSchema,
                      )
                      props.onInputChange({
                        facets: newFacets,
                      })
                    }
                  }}
                  isSelected={isSelected}
                  index={index}
                />
              ) : (
                <FacetOptionItem
                  contentProps={props}
                  option={option}
                  getStateHandler={(state) => {
                    if (state !== isSelected) {
                      const newFacets = updateFacetSelection(
                        state
                          ? UpdateFacetAction.AddFacet
                          : UpdateFacetAction.RemoveFacet,
                        props.facet.facetName,
                        props.facet.facetHeader,
                        option.optionName
                          ? {
                              optionName: option.optionName,
                            }
                          : {
                              from: option.from,
                              to: option.to,
                            },
                        props.selectedFacets,
                        props.useGA4EnhancedEcom,
                        props.facetSchema,
                      )
                      props.onInputChange({
                        facets: newFacets,
                      })
                    }
                  }}
                  isSelected={isSelected}
                  index={index}
                />
              )
            })}
        </StyledListContainer>
      )
    },
    [props, FacetOptionItemInViewComponent],
  )

  const isBrandContent = props.facet.facetName.includes('brand_content')

  if (props.facet.__typename === Facet.RANGED) {
    return getFacetOptionsList(props.facet.options)
  }
  if (props.facet.__typename === Facet.SLIDER) {
    const selectedValues = props.selectedFacets.filter(
      (facet) => facet.facetName === props.facet.facetName,
    )
    return (
      <StyledDivContainer
        data-testid={`facet-slider-container-${props.facet.facetName}`}
        isMobile={props.isMobile}
        maxHeight={props.maxHeight}
      >
        <StyledDivItem>
          <PriceSliderFacet
            maximumPrice={props.facet.maxValue}
            currentMinimumPrice={
              selectedValues.length > 0
                ? (selectedValues[0].selections[0].from as number)
                : 0
            }
            currentMaximumPrice={
              selectedValues.length > 0
                ? (selectedValues[0].selections[0].to as number)
                : props.facet.maxValue
            }
            onChange={({ minimumPrice, maximumPrice }) => {
              const newFacets = updateRangedFacets(
                props.facet.facetName,
                props.facet.facetHeader,
                {
                  from: minimumPrice,
                  to: maximumPrice,
                },
                props.selectedFacets,
                props.useGA4EnhancedEcom,
                props.facetSchema,
              )
              props.onInputChange({
                facets: newFacets,
              })
            }}
            i18nText={props.i18nText}
          />
        </StyledDivItem>
      </StyledDivContainer>
    )
  }

  const filteredOptions = props.facet.options?.filter((option) =>
    isBrandContent
      ? option.optionName.toLowerCase().includes(brandSearchState.toLowerCase())
      : true,
  )

  if (props.facet.__typename === Facet.SIMPLE) {
    return (
      <React.Fragment>
        {isBrandContent && (
          <BrandInputWrapper
            data-testid={`facet-brand-input-container-${props.facet.facetName}`}
            isMobile={props.isMobile}
          >
            <Input
              data-testid={`facet-brand-input-${props.facet.facetName}`}
              label={props.i18nText.searchBrandLabel}
              labelHidden={true}
              placeholder={props.i18nText.searchBrandPlaceholder}
              svgIcon={<SearchIcon />}
              onChange={(event) => brandSearchSetState(event.target.value)}
              clearInputValueHandler={() => brandSearchSetState('')}
              valueOverride={brandSearchState}
              svgIconLeftAlign={false}
            />
          </BrandInputWrapper>
        )}
        {getFacetOptionsList(filteredOptions)}
      </React.Fragment>
    )
  }
  return null
})
